import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { fetchCatalogProgressMeta } from "../api/catalog-api";
import type { ApiClient } from "../api/client";
import { apiClient } from "../api/client";
import type { CatalogProgressMeta } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useCollection } from "./CollectionContext";
import {
  defaultCollectionViewFilters,
  matchesCollectionFilters,
  type CollectionRarityFilter,
  type CollectionViewFilters,
} from "./collection-filters";
import { collectionProgressSummary, seriesFilterOptions, yearFilterOptions } from "./collection-progress";
import { matchesCollectionSearch } from "./collection-search";
import { sortCollectionItems, type CollectionUiSort } from "./collection-sort";
import { CollectionViewContext } from "./CollectionViewContext";

type CollectionViewProviderProps = {
  children: ReactNode;
  client?: ApiClient;
};

function filtersEqual(a: CollectionViewFilters, b: CollectionViewFilters): boolean {
  return (
    a.query === b.query &&
    a.year === b.year &&
    a.series === b.series &&
    a.rarity === b.rarity &&
    a.sort === b.sort
  );
}

export function CollectionViewProvider({
  children,
  client = apiClient,
}: CollectionViewProviderProps) {
  const auth = useAuth();
  const collection = useCollection();
  const [filters, setFilters] = useState<CollectionViewFilters>(
    defaultCollectionViewFilters,
  );
  const [progressMeta, setProgressMeta] = useState<CatalogProgressMeta | null>(null);
  const loadedRef = useRef(false);
  const inflightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (auth.status === "unauthenticated") {
      setFilters(defaultCollectionViewFilters());
      setProgressMeta(null);
      loadedRef.current = false;
      inflightRef.current = null;
    }
  }, [auth.status]);

  const yearOptions = useMemo(
    () => yearFilterOptions(collection.items, progressMeta),
    [collection.items, progressMeta],
  );
  const years = useMemo(() => yearOptions.map((option) => Number(option.value)), [yearOptions]);

  const selectedYear = /^\d{4}$/.test(filters.year) ? Number(filters.year) : null;
  const seriesEnabled = selectedYear !== null;
  const seriesOptions = useMemo(
    () =>
      selectedYear === null
        ? []
        : seriesFilterOptions(collection.items, selectedYear, progressMeta),
    [collection.items, progressMeta, selectedYear],
  );
  const seriesNames = useMemo(
    () => seriesOptions.map((option) => option.value),
    [seriesOptions],
  );

  useEffect(() => {
    if (filters.series !== "" && !seriesNames.includes(filters.series)) {
      setFilters((prev) => ({ ...prev, series: "" }));
    }
  }, [filters.series, seriesNames]);

  const progressItems = useMemo(
    () =>
      collection.items.filter((item) =>
        matchesCollectionFilters(item, {
          year: filters.year,
          series: filters.series,
          rarity: "",
        }),
      ),
    [collection.items, filters.series, filters.year],
  );

  const visibleItems = useMemo(() => {
    const matched = collection.items.filter(
      (item) =>
        matchesCollectionFilters(item, filters) &&
        matchesCollectionSearch(item, filters.query),
    );
    return sortCollectionItems(matched, filters.sort);
  }, [collection.items, filters]);

  const progress = useMemo(
    () => collectionProgressSummary(progressItems, filters, progressMeta),
    [filters, progressItems, progressMeta],
  );

  const emptyKind = useMemo(() => {
    if (collection.status !== "ready") {
      return "none" as const;
    }
    if (collection.items.length === 0) {
      return "collection" as const;
    }
    if (visibleItems.length > 0) {
      return "none" as const;
    }
    if (filters.query.trim() !== "") {
      return "search" as const;
    }
    if (filters.series !== "") {
      return "series" as const;
    }
    if (filters.rarity !== "") {
      return "rarity" as const;
    }
    if (filters.year !== "") {
      return "year" as const;
    }
    return "filters" as const;
  }, [
    collection.items.length,
    collection.status,
    filters.query,
    filters.rarity,
    filters.series,
    filters.year,
    visibleItems.length,
  ]);

  const setQuery = useCallback((query: string) => {
    setFilters((prev) => (prev.query === query ? prev : { ...prev, query }));
  }, []);

  const setYear = useCallback((year: string) => {
    setFilters((prev) => {
      if (prev.year === year) {
        return prev;
      }
      return { ...prev, year, series: "" };
    });
  }, []);

  const setSeries = useCallback((series: string) => {
    setFilters((prev) => (prev.series === series ? prev : { ...prev, series }));
  }, []);

  const setRarity = useCallback((rarity: CollectionRarityFilter) => {
    setFilters((prev) => (prev.rarity === rarity ? prev : { ...prev, rarity }));
  }, []);

  const setSort = useCallback((sort: CollectionUiSort) => {
    setFilters((prev) => (prev.sort === sort ? prev : { ...prev, sort }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters((prev) =>
      filtersEqual(prev, defaultCollectionViewFilters())
        ? prev
        : defaultCollectionViewFilters(),
    );
  }, []);

  const ensureProgressMeta = useCallback(async () => {
    if (loadedRef.current || auth.status !== "authenticated") {
      return;
    }
    if (inflightRef.current) {
      await inflightRef.current;
      return;
    }
    const request = (async () => {
      try {
        const meta = await fetchCatalogProgressMeta(client);
        setProgressMeta(meta);
        loadedRef.current = true;
      } catch {
        setProgressMeta({ years: [] });
        loadedRef.current = true;
      } finally {
        inflightRef.current = null;
      }
    })();
    inflightRef.current = request;
    await request;
  }, [auth.status, client]);

  const value = useMemo(
    () => ({
      filters,
      years,
      yearOptions,
      seriesNames,
      seriesOptions,
      seriesEnabled,
      visibleItems,
      progressItems,
      progress,
      progressMeta,
      emptyKind,
      setQuery,
      setYear,
      setSeries,
      setRarity,
      setSort,
      resetFilters,
      ensureProgressMeta,
    }),
    [
      emptyKind,
      ensureProgressMeta,
      filters,
      progress,
      progressItems,
      progressMeta,
      resetFilters,
      seriesEnabled,
      seriesNames,
      seriesOptions,
      setQuery,
      setRarity,
      setSort,
      setSeries,
      setYear,
      visibleItems,
      yearOptions,
      years,
    ],
  );

  return (
    <CollectionViewContext.Provider value={value}>
      {children}
    </CollectionViewContext.Provider>
  );
}
